import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BuilderPage } from '@makyoapp/makyo-common'

import { parsePageTitle, parseVariableTextSingleCell } from '@/core/app-builder/parser/variable-text'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'

import { useTableSubscription } from './useTableSubscription'

/**
 * @note only use this hook inside app builder preview
 *
 * @deprecated
 */
export const usePageTitle = (page?: BuilderPage) => {
  const params = useParams()
  const currentRowId = params['rowId'] || ''

  const { userEmail } = useAppAuthContext()

  const { dbClient, language, userSpecific } = useDbClientContext()
  const { appClient } = useAppBuilderContext()
  const { activePage } = useAppPagesContext()

  const currentPage = page ?? activePage
  const tableId = currentPage?.tableId || ''

  const tableIds = useMemo(() => {
    if (!tableId) return []
    return [tableId]
  }, [tableId])

  const [pageTitle, setPageTitle] = useState('')

  const handleGetPageTitle = useCallback(
    (value: string, pageId: string, currentRowId?: string) => {
      const client = appClient.pagesMap.get(pageId)

      // handle detail page
      if (!!currentRowId && client) {
        setPageTitle(parsePageTitle(client, language, userSpecific, currentRowId))
      } else {
        // handle regular page, might use variable text (single cell)
        if (isDescendant(value) && !!client) {
          let node = parseStringToDescendant(value)

          setPageTitle(parseVariableTextSingleCell(node, dbClient, language, userSpecific, userEmail))
        } else {
          setPageTitle(value)
        }
      }
    },
    [appClient.pagesMap, dbClient, language, userEmail, userSpecific]
  )

  const handleUpdatePageTitle = useCallback(() => {
    if (!currentPage) return
    handleGetPageTitle(currentPage.title, currentPage.id, currentRowId)
  }, [currentPage, currentRowId, handleGetPageTitle])

  // Subscribe to table changes if it's a detail page
  useTableSubscription({
    dbClient,
    tableIds,
    callback: handleUpdatePageTitle,
  })

  return pageTitle
}
